// ═══════════════════════════════════════════════════════════════════════════
// Qué se puede hacer con una remisión — lote 2.6, unificación de la interfaz
//
// EL DEFECTO QUE ESTO CORRIGE, observado por el operador: la misma remisión
// tenía poderes distintos según la pantalla por la que se llegara. En
// Historial se podía timbrar, editar y cancelar; en Cuentas solo bajar PDFs;
// en Clientes ni siquiera se veía.
//
// Eso no fue una decisión de diseño, fue sedimento: cada pantalla creció con
// lo que hizo falta ese día. Y producía un absurdo — Cuentas tiene una lista
// llamada "por facturar" y era el único lugar donde NO se podía facturar.
//
// LA REGLA AHORA: las acciones las decide el ESTADO DEL DOCUMENTO y el ROL de
// quien mira, nunca la pantalla. Encuentres la remisión donde la encuentres,
// puedes hacer con ella exactamente lo mismo.
//
// DUPLICAR SE QUITÓ, por decisión del operador: "es una optimización gratuita
// que puede confundir". Partía de un documento viejo elegido a mano y dejaba
// dos remisiones parecidas sin relación declarada — fácil de confundir con un
// cobro doble. Lo que sustituye es la sugerencia del pedido usual al EMPEZAR
// una remisión nueva (dominio/pedido-usual.js), que es donde sirve.
//
// Aquí solo vive la decisión, sin nada de React: qué botones existen, cuál va
// al frente y por qué uno no está disponible. Así se puede probar de verdad,
// que es donde importa — equivocarse aquí es ofrecer "Editar" en algo ya
// timbrado, o "Timbrar" a quien no le corresponde.
// ═══════════════════════════════════════════════════════════════════════════

import { puede } from "../auth/permisos.js";

/** Día y mes, para distinguir varios complementos sin llenar el botón. */
const fechaCorta = (f) => {
  const d = new Date(f);
  return isNaN(d) ? "" : `${d.getDate()}/${d.getMonth() + 1}`;
};

/**
 * Estado fiscal del documento. Manda sobre todo lo demás: una remisión
 * cancelada no se edita aunque el rol lo permita.
 *
 *   cancelada    → la remisión misma se canceló (el folio queda consumido)
 *   cfdi_cancelado → tuvo CFDI y se canceló ante el SAT; vuelve a ser editable
 *   timbrada     → tiene CFDI vigente; intocable
 *   borrador     → todavía no se timbra
 */
export function estadoRemision(inv) {
  if (inv?.canceledAt) return "cancelada";
  if (inv?.cfdiId || inv?.cfdiUuid) {
    return inv.cfdiStatus === "canceled" ? "cfdi_cancelado" : "timbrada";
  }
  return "borrador";
}

/** ¿El cliente tiene lo mínimo que exige el SAT para timbrarle? */
export function tieneDatosFiscales(cliente) {
  return !!(cliente?.rfc && cliente?.taxSystem &&
            cliente?.domicilioFiscalZip && cliente?.legalName);
}

// Cada acción declara qué permiso necesita. Que esté aquí y no repartido por
// las pantallas es justamente el punto: antes, timbrar estaba protegido por
// accidente —vivía en un solo componente— y no por decisión.
//
// LO QUE NO PUEDES HACER, NO SE DIBUJA — regla del operador, dicha sobre los
// iconos de timbrar y de marcar liquidada: "no deberían aparecer; ni siquiera
// apagados. Acciones que no puede ejecutar un rol, no deben aparecer en su UI."
//
// Antes se devolvían con un `bloqueo` y la barra las pintaba al 35% con un
// candado. La intención era buena —explicar en vez de esconder— y estaba
// equivocada por tres razones que se ven en uso:
//
//   · Un icono apagado sigue siendo un ofrecimiento. Invita a picarle, y lo
//     que contesta es un regaño. El vendedor no aprende nada útil de saber
//     que existe un botón de cancelar CFDI que nunca va a poder usar.
//   · Enseña el mapa de lo que no le toca: cuántas cosas hay, cómo se llaman.
//   · Es el mismo criterio que ya regía las PESTAÑAS desde antes —"una pestaña
//     que solo sabe decir 'no puedes' es peor que no estar"—; las acciones
//     simplemente no se habían alineado.
//
// Ojo con la diferencia que sí importa: se oculta lo que el ROL no permite. Lo
// que el ESTADO del documento no permite ya se omitía (no hay "Editar" en una
// timbrada), y lo que le FALTA al documento se sigue diciendo, porque eso el
// usuario sí lo puede resolver: de ahí el aviso de la CSF.
const PERMISO = {
  timbrar:          "timbrar_cfdi",
  cancelar_cfdi:    "cancelar_cfdi",
  cancelar_remision: "cancelar_remision",
  editar:           "editar_remision",
  pagos:            "registrar_pago",
  reasignar:        "asignar_cartera",
};

/**
 * Las acciones que aplican a una remisión, ya ordenadas: primero la que toca
 * hacer ahora, después el resto.
 *
 * @param inv           la remisión
 * @param rol           'owner' | 'manager' | 'accountant' | 'agent'
 * @param cliente       el cliente AL DÍA (no el del snapshot: la CSF pudo
 *                      subirse después de emitir la remisión)
 * @param facturapiActivo  si hay timbrado configurado
 * @returns [{ id, etiqueta, icono, tono, primaria }]
 *          Todas las que vuelven se pueden ejecutar: lo que el rol no permite
 *          no aparece, y lo que el estado no permite tampoco. Lo único que
 *          vuelve sin ser botón son los avisos (`esAviso`), que informan de
 *          algo que al documento le falta y el usuario sí puede resolver.
 */
export function accionesRemision(inv, { rol, cliente, facturapiActivo = false } = {}) {
  const estado = estadoRemision(inv);
  const acciones = [];
  const agregar = (a) => acciones.push({ tono: "neutro", primaria: false, ...a });

  // La puerta de cada acción. Se pregunta por el PERMISO, no por el id: son
  // nombres distintos —"timbrar" contra "timbrar_cfdi"— y confundirlos ya
  // había producido un aviso sin sentido, "solo puede realizarla nadie".
  const permitida = (id) => {
    const p = PERMISO[id];
    return !p || puede(rol, p);
  };

  // ── Timbrar: solo tiene sentido en un borrador ──────────────────────────
  // El bloque entero cuelga del permiso, incluido el aviso de la CSF: a quien
  // no timbra, avisarle que falta la CSF "para timbrar" es hablarle de un
  // trabajo que no es suyo.
  //
  // Y en una cuyo CFDI se canceló: es como se reemite una factura —se cancela,
  // se corrige la remisión si hace falta, y se vuelve a timbrar con el mismo
  // folio. El operador, al reemitir la VT432 con IEPS desglosado: "en el pdf
  // aparece cancelado, pero no hay opción de volver a timbrar". El servidor
  // ya lo permitía; lo único que impide es timbrar con un CFDI VIGENTE.
  const reemitir = estado === "cfdi_cancelado";
  if ((estado === "borrador" || reemitir) && facturapiActivo && permitida("timbrar")) {
    if (!tieneDatosFiscales(cliente)) {
      // No es un botón: es un aviso. Ofrecer un botón que siempre falla es
      // peor que explicar qué falta.
      agregar({ id: "falta_csf", etiqueta: "Sube la CSF del cliente para timbrar",
                icono: "⚠️", tono: "aviso", esAviso: true });
    } else {
      agregar({ id: "timbrar", etiqueta: reemitir ? "Volver a timbrar" : "Timbrar CFDI",
                icono: "🏛", tono: "exito", primaria: true });
    }
  }

  // ── Cobranza: se puede pagar mientras el documento viva ─────────────────
  // Antes solo existía en Cuentas. Una remisión timbrada de hace tres meses
  // aparecía en Historial sin manera de registrar que ya la pagaron.
  if (estado !== "cancelada" && permitida("pagos")) {
    agregar({ id: "pagos", etiqueta: "Pagos", icono: "💰",
              primaria: estado === "timbrada" });
  }

  // ── Documentos propios: siempre ─────────────────────────────────────────
  agregar({ id: "pdf_remision", etiqueta: "PDF Remisión", icono: "📄" });
  agregar({ id: "pdf_envio", etiqueta: "PDF Envío", icono: "📦", tono: "envio" });

  // ── Documentos del SAT: solo si hubo timbrado ───────────────────────────
  if (estado === "timbrada" || estado === "cfdi_cancelado") {
    // ── Los dos juntos, en un toque ─────────────────────────────────────
    //
    // El operador: "los clientes piden el pdf y el xml para sus archivos; se
    // deberían poder compartir desde el teléfono con un solo click también."
    //
    // En TODO aparato, desde 0.59. Antes solo donde había hoja de compartir,
    // porque la acción abría la hoja directo. Ahora abre el visor con el XML
    // a bordo, y el visor decide: en un teléfono comparte los dos, en un
    // escritorio los descarga. El operador lo pidió así: "debería tener un
    // botón para descargar pdf+xml".
    agregar({ id: "compartir_cfdi", etiqueta: "Factura PDF+XML", icono: "📤",
              tono: "exito" });
    agregar({ id: "pdf_cfdi", etiqueta: "PDF CFDI", icono: "🏛", tono: "exito" });
    agregar({ id: "xml_cfdi", etiqueta: "XML CFDI", icono: "📋", tono: "exito" });

    // ── Los complementos, DONDE está el documento que amparan ─────────────
    //
    // Una PPD con pagos tiene DOS comprobantes o más: la factura y un REP por
    // cada pago. La remisión enseñaba solo el primero, así que para el otro
    // había que acordarse de entrar a Pagos — el operador lo reportó así.
    //
    // Se ofrecen desde aquí porque el complemento es del mismo documento: quien
    // busca "los papeles de la remisión 646" los quiere todos juntos.
    const reps = (inv.payments || []).filter(p => p.rep?.facturapiId);
    for (const p of reps) {
      agregar({ id: `pdf_rep:${p.id}`, icono: "🧾", tono: "exito",
                etiqueta: reps.length > 1 ? `PDF REP ${fechaCorta(p.fecha)}` : "PDF complemento" });
      agregar({ id: `xml_rep:${p.id}`, icono: "📋", tono: "exito",
                etiqueta: reps.length > 1 ? `XML REP ${fechaCorta(p.fecha)}` : "XML complemento" });
    }
  }
  if (estado === "cfdi_cancelado") {
    agregar({ id: "acuse", etiqueta: "Acuse Cancelación", icono: "📄", tono: "peligro" });
  }

  // ── Editar: la regla fiscal manda sobre el permiso ──────────────────────
  // Con CFDI vigente no se edita ni siendo dueño: el comprobante ya existe
  // ante el SAT y editarlo aquí solo desalinearía los dos.
  if ((estado === "borrador" || estado === "cfdi_cancelado") && permitida("editar")) {
    agregar({ id: "editar", etiqueta: "Editar", icono: "✏️", tono: "edicion" });
  }

  // ── De quién es esta venta ──────────────────────────────────────────────
  //
  // Se puede corregir mientras el documento viva. En una cancelada no: no hay
  // nada que atribuirle a nadie.
  //
  // No depende del estado fiscal — una remisión timbrada puede estar a nombre
  // de quien no la vendió, y eso se arregla sin tocar el CFDI: el comprobante
  // dice quién compró, no quién cobró la comisión.
  if (estado !== "cancelada" && permitida("reasignar")) {
    agregar({ id: "reasignar", etiqueta: "Cambiar vendedor", icono: "🤝",
              tono: "edicion" });
  }

  // ── Cancelaciones ───────────────────────────────────────────────────────
  // Una cancelación solicitada que espera al receptor no se vuelve a pedir:
  // se consulta. El CFDI sigue vigente hasta que el SAT diga otra cosa.
  if (estado === "timbrada" && permitida("cancelar_cfdi")) {
    if (inv.cfdiCancelacionPendiente)
      agregar({ id: "consultar_cancelacion", etiqueta: "Consultar cancelación",
                icono: "⏳", tono: "peligro" });
    else
      agregar({ id: "cancelar_cfdi", etiqueta: "Cancelar CFDI", icono: "🚫",
                tono: "peligro" });
  }
  if ((estado === "borrador" || estado === "cfdi_cancelado")
      && permitida("cancelar_remision")) {
    // Un CFDI vigente bloquea la cancelación de la remisión: cancelar aquí no
    // cancelaría el comprobante ante el SAT. Misma regla que en la base
    // (migración 0009), escrita en los dos lados a propósito.
    agregar({ id: "cancelar_remision", etiqueta: "Cancelar remisión", icono: "🚫",
              tono: "peligro" });
  }

  return acciones;
}

/** Las que el usuario puede ejecutar de verdad. Útil para pruebas y para
 *  decidir si vale la pena mostrar una barra de acciones.
 *
 *  Desde que lo prohibido no se dibuja, esto es `accionesRemision` sin los
 *  avisos. Se conserva porque el nombre dice la intención en quien lo lee, y
 *  porque es el punto donde una prueba puede fijar la regla. */
export const accionesUtiles = (inv, ctx) =>
  accionesRemision(inv, ctx).filter(a => !a.esAviso);
